/**
 * @fileoverview Hook for Regrid master-cache file metadata used by the
 *              onboarding wizard Step 9 parcel import section.
 * @module system-web/hooks/useParcelCacheFiles
 *
 * Responsibilities:
 * - Subscribe to the `geo_cache_files` Dexie table via liveQuery.
 * - Trigger a background refresh of cache file metadata on mount.
 * - Provide a helper to auto-match a cache file by FIPS or state + county.
 *
 * Dependencies:
 * - parcelDataAdminService (listCacheFiles, refreshCacheFiles, GeoCacheFile).
 * - dexie/liveQuery.
 *
 * Architecture:
 * - liveQuery observed function is a pure Dexie read (no writes).
 * - refreshCacheFiles is called in a separate mount effect, never inside liveQuery.
 *
 * Notes:
 * - Cache files are not part of the standard sync pipeline; they are fetched
 *   on demand from system-api and cached locally.
 *
 * @localFirst
 * Source of Truth: `geo_cache_files` Dexie table (local cache, system-api refresh).
 * Write Flow: service writes to Dexie; hook reads via liveQuery.
 * Sync Strategy: on-demand fetch from system-api.
 * Conflict Resolution: n/a (server-authoritative cache).
 *
 * @hook useParcelCacheFiles
 *
 * Purpose:
 * - Load and keep Regrid master-cache file metadata up to date for the
 *   onboarding wizard's auto-match UX.
 *
 * Returns:
 * - files:            Live list of GeoCacheFile rows from Dexie.
 * - loading:          True before the first liveQuery emission.
 * - error:            Non-null when refresh or liveQuery fails.
 * - findAutoMatchedFile: Filter helper for FIPS / state+county matching.
 *
 * Side Effects:
 * - On mount: calls refreshCacheFiles() and subscribes to listCacheFiles().
 *
 * Offline Behavior:
 * - Files from the last successful refresh are available offline.
 * - Refresh requires connectivity and is best-effort.
 */
import { useState, useEffect, useCallback } from 'react';
import { liveQuery } from 'dexie';
import {
  listCacheFiles,
  refreshCacheFiles,
  type GeoCacheFile,
} from '../services/parcelDataAdminService';

export interface UseParcelCacheFilesResult {
  files: GeoCacheFile[];
  loading: boolean;
  error: Error | null;
  findAutoMatchedFile: (
    fipsCode?: string,
    stateCode?: string,
    countyName?: string,
    stateName?: string,
  ) => GeoCacheFile | null;
}

/**
 * @process normalizeFips
 * Purpose: Compare FIPS codes irrespective of lost leading zeros. Numeric
 *          pipelines routinely turn "06059" into "6059", so both sides are
 *          left-padded to 5 digits before comparison.
 * Inputs:  Raw FIPS-ish string.
 * Outputs: 5-digit zero-padded string, or '' when there are no digits.
 * Failure Handling: Non-numeric input degrades to '' and never matches.
 * Idempotency: REQUIRED — pure.
 */
function normalizeFips(value?: string): string {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits ? digits.padStart(5, '0') : '';
}

/**
 * @process normalizeCountyName
 * Purpose: Reduce a county label to a comparable core name by stripping the
 *          census-area suffix and punctuation. "Orange County" and "Orange"
 *          must compare equal because the lookup catalog and the geo cache
 *          catalog disagree on whether the suffix is included.
 * Inputs:  County display name.
 * Outputs: Lowercased, suffix-stripped, whitespace-collapsed name.
 * Failure Handling: Empty/undefined input yields ''.
 * Idempotency: REQUIRED — pure.
 */
function normalizeCountyName(value?: string): string {
  return (value ?? '')
    .toLowerCase()
    .replace(/\b(county|parish|borough|census area|municipality|city and borough|municipio)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * @process stateMatches
 * Purpose: Decide whether a cache file belongs to the requested state when the
 *          two catalogs express state differently. `geoCacheFiles.state` is a
 *          mix of 2-letter postal codes ("nc", "in") and 2-digit state FIPS
 *          ("06", "48"), while the jurisdiction lookup may supply either form
 *          (duplicate California rows exist as both "CA" and "06"). Rather
 *          than duplicating a 50-row postal-to-FIPS table in the client, the
 *          state *name* — which both catalogs carry — is used as the bridge.
 * Inputs:  file — the cache file row; stateCode — postal or FIPS state code;
 *          stateName — the jurisdiction state display name (optional).
 * Outputs: True when the file is in the requested state.
 * Failure Handling: Returns false when no form can be compared.
 * Idempotency: REQUIRED — pure.
 */
function stateMatches(file: GeoCacheFile, stateCode: string, stateName?: string): boolean {
  const want = stateCode.trim().toLowerCase();
  const fileState = (file.state ?? '').trim().toLowerCase();

  // Same representation on both sides (postal-to-postal or FIPS-to-FIPS).
  if (fileState && fileState === want) return true;

  // Requested a 2-digit state FIPS — compare against the leading two digits of
  // the county FIPS, which is always present and unambiguous.
  if (/^\d{1,2}$/.test(want)) {
    const fips = normalizeFips(file.fipsCode || file._id);
    if (fips.length < 2) return false; // Defensive: malformed/empty FIPS
    return fips.slice(0, 2) === want.padStart(2, '0');
  }

  // Requested a postal code but the file stores a numeric state (or vice
  // versa) — bridge via the state name, which both catalogs carry.
  const wantName = (stateName ?? '').trim().toLowerCase();
  const fileName = (file.stateName ?? '').trim().toLowerCase();
  return Boolean(wantName && fileName && wantName === fileName);
}

export function useParcelCacheFiles(): UseParcelCacheFilesResult {
  const [files, setFiles] = useState<GeoCacheFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // ---------------------------------------------------------------------------
  // On mount: refresh cache file metadata from system-api. This is a write to
  // Dexie and MUST live in its own effect, not inside the liveQuery below.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    refreshCacheFiles()
      .then(async () => {
        // The liveQuery below does not always re-emit for this write (the
        // refresh can land through a Dexie write path the observable misses),
        // so surface the refreshed rows explicitly. The subscription remains
        // the source of truth for subsequent mutations (uploads, deletes).
        if (!cancelled) {
          const rows = await listCacheFiles();
          if (!cancelled) {
            setFiles(rows);
            setLoading(false);
          }
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      });
    return () => { cancelled = true; };
  }, []);

  // ---------------------------------------------------------------------------
  // liveQuery: pure read of `geo_cache_files`, no writes.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const subscription = liveQuery(() => listCacheFiles()).subscribe({
      next: (rows) => {
        setFiles(rows);
        setLoading(false);
      },
      error: (err: unknown) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      },
    });
    return () => subscription.unsubscribe();
  }, []);

  const findAutoMatchedFile = useCallback(
    (
      fipsCode?: string,
      stateCode?: string,
      countyName?: string,
      stateName?: string,
    ): GeoCacheFile | null => {
      if (!fipsCode && (!stateCode || !countyName)) return null;

      // Tier 1 — FIPS code. The only fully reliable key: geoCacheFiles._id and
      // .fipsCode are both the 5-digit state+county FIPS, and county lookups
      // carry the same code. Independent of state/county string formatting.
      if (fipsCode) {
        const byFips = files.find(
          (f) => normalizeFips(f.fipsCode) === normalizeFips(fipsCode) || normalizeFips(f._id) === normalizeFips(fipsCode),
        );
        if (byFips) return byFips;
      }

      if (!stateCode || !countyName) return null;

      // Tier 2 — normalized state + county name. Needed for legacy drafts that
      // predate jurisdiction.fipsCode. County names carry inconsistent
      // "County"/"Parish"/"Borough" suffixes, and `geoCacheFiles.state` is a
      // mix of 2-letter postal codes ("nc", "in") and 2-digit state FIPS
      // ("06", "48"), so both sides are normalized before comparing.
      const wantCounty = normalizeCountyName(countyName);
      return (
        files.find(
          (f) => stateMatches(f, stateCode, stateName) && normalizeCountyName(f.county) === wantCounty,
        ) ?? null
      );
    },
    [files],
  );

  return { files, loading, error, findAutoMatchedFile };
}
